import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngxs/store';
import { first, catchError, map } from 'rxjs/operators';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { RegistrationState, NotificationStateModel } from 'src/app/registration/state/registration.state';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { SubSink } from 'subsink';
import { DQF_DOC_STATUS } from '../constants';
import { DriverQualificationService } from '../service/driver-qualification.service';
import { DRIVER_DOC_CONTEXT_INITIAL_DQF } from '../constants';
import { of } from 'rxjs';
import { ComponentsModule } from 'src/app/shared/components/components.module';
import { DqfDocumentSkeletonComponent } from '../dqf-document-skeleton/dqf-document-skeleton.component';

const REPORT_TYPE = 'ClearingHouseReport';
const ACTION_UPLOAD_DOCUMENT = 'Upload Document';
const UPLOADED_STATUS = 'uploaded';
const ACTION_DELETE_STATUS = 'Deleted Document';
const UPLOADED_DOCUMENT = 'Successfully uploaded document';
const DELETE_MESSAGE = 'Are you sure you want to delete this document? This action cannot be undone.';
const DELETED_DOCUMENT = 'Document deleted successfully';
const REQUEST_SUCESS = 'Successfully sent the request';

const DISABLE_TOP_LOADING_BAR = true;

@Component({
    selector: 'app-dqf-clear-house',
    templateUrl: './dqf-clear-house.component.html',
    styleUrls: ['./dqf-clear-house.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        ReactiveFormsModule,
        ComponentsModule,
        DqfDocumentSkeletonComponent
    ]
})
export class DqfClearHouseComponent implements OnInit {
  form: UntypedFormGroup;
  formUploadFiles = { form: null };
  fileInfo: { id: string; fileName: string; generationDate: string };
  subSink = new SubSink();
  isContentReady: boolean = false;
  documentStatus: string;
  documentType = REPORT_TYPE;
  constructor(
    private readonly location: Location,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly driverQualificationService: DriverQualificationService,
    private readonly toastService: ToastService,
    private readonly registrationService: RegistrationService,
    private readonly store: Store
  ) {}

  ngOnInit(): void {
    this.getDocumentDqfStatus();
    this.loadDriverDocuments();
    this.form = this.createForm();
    this.formUploadFiles = {
      form: this.form
    };
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  backAction(): void {
    this.location.back();
  }
  getDocumentDqfStatus(): void {
    const document = this.driverQualificationService.getDqfDocumentFromAttachedDocuments(REPORT_TYPE);
    if (!document) {
      return;
    }

    this.documentStatus = document.status;
  }
  uploadDocument(): void {
    this.isContentReady = false;
    const model = this.driverQualificationService.prepareUploadDocumentSaveModel(
      this.form.value,
      REPORT_TYPE,
      DRIVER_DOC_CONTEXT_INITIAL_DQF,
      this.driverQualificationService.dqfId
    );
    if (model && this.formUploadFiles.form.valid) {
      this.subSink.sink = this.driverQualificationService
        .uploadDqfDocument(model, this.form.value.file, DISABLE_TOP_LOADING_BAR)
        .pipe(
          first(),
          catchError((error: any) => {
            this.isContentReady = true;
            this.formUploadFiles.form.get('file').patchValue(null);
            this.formUploadFiles = { ...this.formUploadFiles };

            return of(false);
          })
        )
        .subscribe(() => {
          const docModel = {
            attachedDocument: {
              type: REPORT_TYPE,
              status: DQF_DOC_STATUS.DRIVER_REVIEWED
            }
          };
          this.updateDqfDocStatus(docModel, UPLOADED_DOCUMENT);
        });
    }
  }
  requestCarrierToUploadDocument(): void {
    const docModel = {
      attachedDocument: {
        type: REPORT_TYPE,
        status: DQF_DOC_STATUS.CARRIER_UPLOAD_REQUESTED
      }
    };
    this.updateDqfDocStatus(docModel, REQUEST_SUCESS);
  }
  deleteDocument(): void {
    if (confirm(DELETE_MESSAGE)) {
    this.isContentReady = false;
    if (this.formUploadFiles.form?.value?.file?.id) {
      this.subSink.sink = this.driverQualificationService
        .deleteDqfDocument(this.formUploadFiles.form?.value?.file?.id, DISABLE_TOP_LOADING_BAR)
        .subscribe(
          () => {
            this.isContentReady = true;
            this.toastService.showSuccess(DELETED_DOCUMENT);
            this.form.reset();
            this.formUploadFiles.form.get('file').patchValue(null);
          },
          error => {
            this.isContentReady = true;
            return of(false);
          }
        );
    }
  }
  }

  loadDriverDocuments(): void {
    this.isContentReady = false;
    this.subSink.sink = this.registrationService
      .loadDriver(DISABLE_TOP_LOADING_BAR)
      .pipe(
        map(() => this.store.selectSnapshot(RegistrationState.notification)),
        catchError((error: any) => {
          console.error('Error loading driver documents:', error);
          this.isContentReady = true; // Set to true even on error so content shows
          return of(null);
        })
      )
      .subscribe((notificationStateModel: NotificationStateModel) => {
        if (notificationStateModel && notificationStateModel.notifications) {
          const filteredValue = notificationStateModel.notifications.filter(
            notification =>
              notification.type === REPORT_TYPE &&
              notification.status == UPLOADED_STATUS &&
              notification?.dqfId === this.driverQualificationService.dqfId
          );
          this.isContentReady = true;
          if (filteredValue.length) {
            this.driverQualificationService.bindDriverDocument.call(this, filteredValue[0], this.formUploadFiles);
          }
        } else {
          // Even if no notifications, set content ready so the upload form shows
          this.isContentReady = true;
        }
      });
  }

  updateDqfDocStatus(docModel: any, successMessage: string): void {
    this.subSink.sink = this.driverQualificationService
      .updateDqfStatus(this.driverQualificationService.dqfId, docModel)
      .subscribe(() => {
        this.toastService.showSuccess(successMessage);
        // Refresh the document data instead of navigating away
        this.loadDriverDocuments();
      });
  }

  private createForm(): UntypedFormGroup {
    return this.formBuilder.group({
      generationDate: ['', Validators.required],
      file: ['', Validators.required]
    });
  }
}
